
import { Button, Tooltip, useMediaQuery, Box, Typography } from "@mui/material";
import React, { useState } from "react";
import { normalizePhoneNumber } from "../utils/normalizePhoneNumber.js"; 
import { Snackbar, Alert } from "@mui/material";
import { logDev } from "../utils/logger.js";

// 🔹 Composant principal : BoutonWhatsApp
// - Affiche un bouton WhatsApp qui permet de générer un message d’invitation
// - Normalise et valide les numéros de téléphone (FR, ES, EN, US)
// - Désactive le bouton si aucun numéro valide n’est trouvé

const BoutonWhatsApp = ({ nomGroupe, contacts, t }) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  // ✅ Normalisation multi-pays
  // Essaie de transformer un numéro brut en format international
  // en testant plusieurs pays (FR, ES, EN, US)
  const normalizeMultiCountry = (num, countries = ["FR", "ES", "EN", "US"]) => {
    for (const c of countries) {
      const normalized = normalizePhoneNumber(num, c);
      // ✅ Vérifie que le résultat est bien au format international
      if (/^\+\d{7,15}$/.test(normalized)) {
        return normalized;
      }
    }
      return null; // aucun pays n’a matché
    };

  // 🔎 Extraction et validation des numéros depuis la liste de contacts
  // - On normalise chaque numéro
  // - On filtre pour ne garder que les valides

  const phones = contacts
    .map(c => normalizeMultiCountry(c.telephone || ""))
    .filter(Boolean);

  // ✅ Indique si on a au moins un numéro valide
  const hasPhones = phones.length > 0;

  // ✅ Action au clic sur le bouton
  // - Génère un message d’invitation avec le nom du groupe
  // - Ouvre WhatsApp Web avec ce message pré-rempli
  const handleClick = () => {
  if (!hasPhones) return;

  const message = `📣 ${t("CreateWhatsAppGroup")}\n\n${phones.join(", ")}\n\n`;

  if (isMobile) {
    alert(t("CreateWhatsAppGroup"));
  }

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, "_blank");
};

const handleCopy = async () => {
  logDev("📋 handleCopy déclenché", { isMobile, phones });
  logDev("📋 Tentative de copie des numéros", {
    clipboard: navigator.clipboard,
    phones
  });

  if (!navigator.clipboard || !navigator.clipboard.writeText) {
    logDev("❌ Clipboard API non disponible", { environment: navigator.userAgent });
    setSnackbar({
      open: true,
      message: "Copie non supportée sur ce navigateur",
      severity: "warning"
    });
    return;
  }

  try {
    await navigator.clipboard.writeText(phones.join(", "));
    logDev("✅ Numéros copiés avec succès");
    setSnackbar({
      open: true,
      message: "Numéros copiés dans le presse-papier",
      severity: "success"
    });
  } catch (err) {
    logDev("❌ Erreur lors de la copie", { message: err.message, stack: err.stack });
    setSnackbar({
      open: true,
      message: "Impossible de copier automatiquement",
      severity: "error"
    });
  }
};

  const [snackbar, setSnackbar] = useState({
  open: false,
  message: "",
  severity: "info"
    });

const handleClose = () => setSnackbar({ ...snackbar, open: false });

  // ✅ Définition du bouton WhatsApp
  const button = (
    <Button
      variant="contained"
      onClick={handleClick}
      disabled={!hasPhones}
      sx={{
        backgroundColor: "#25D366 !important", // vert WhatsApp
        color: "#fff !important",
        marginLeft: 1,
        marginTop: 1,
        fontWeight: "bold",
        fontSize: isMobile ? "1.5rem" : "2rem",
        transition: "all 0.3s ease",
        width: 60,
        height: 60,
        borderRadius: "50%",
        minHeight: isMobile ? "10px" : "20px",
        minWidth: isMobile ? "20px" : "auto",
        padding: 1,
        
        "&:hover": {
          backgroundColor: "#128C7E",
          transform: "scale(1.05)"
        }
      }}
    >
      📱
    </Button>
  );

  // ✅ Rendu final
  // - Sur mobile : affiche directement le bouton
  // - Sur desktop : affiche le bouton avec un Tooltip explicatif
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
  {isMobile ? button : (
    <Tooltip
      title={hasPhones ? t("CreateWhatsAppGroup") : t("NoValidPhone")}
      arrow
      placement="top"
    >
      <span>{button}</span>
    </Tooltip>
  )}

  <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "#666", mt: 0 }}>
    {phones.length === 0
      ? t("NoValidPhone")
      : `${phones.length} ${phones.length === 1 ? t("ValidPhoneNumber") : t("ValidPhoneNumbers")}`}
  </Typography>

  {hasPhones && (
    <>
    {logDev("📋 Bouton copier rendu", { isMobile, hasPhones })}
    {/*<Button
      onClick={handleCopy}
      size="small"
      variant="outlined"
      sx={{ mt: 1, fontSize: "0.6rem" }}
    >
      Copier les numéros
    </Button>*/}
    {!navigator.clipboard && (
  <Box sx={{ mt: 1, p: 1, border: "1px dashed #ccc", borderRadius: 1 }}>
    <Typography variant="caption" sx={{ fontStyle: "italic", color: "#999" }}>
      Sélectionne et copie manuellement :
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word", fontSize: "0.7rem" }}>
      {phones.join(", ")}
    </Typography>
  </Box>
)}

    </>
  )}
  <Snackbar
  open={snackbar.open}
  autoHideDuration={3000}
  onClose={handleClose}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: "100%" }}>
    {snackbar.message}
  </Alert>
</Snackbar>
</Box>

  );
  
};

export default BoutonWhatsApp;